import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  useHealthieInfo,
  useI18n,
  usePersonalInfo,
  useRehabSelectedThemes,
} from 'contexts/AuthContext'
import { Button, Input } from 'antd'
import classnames from 'classnames'
import _ from 'lodash'
import { isMobile, showNotification } from 'utils/basic'
import ContentHeader from './ContentHeader'
import {
  getHealthieUserId,
  getUserEmail,
  setStateId,
} from 'utils/authLocalStorage'
import { getLocalLanguage } from 'utils/authLocalStorage'
import {
  emailMagicLinkPromise,
  updateUserPromise,
  verifyMagicLinkByDigitCodePromise,
} from 'apis/FitOnCareAPI'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { applySkuTag } from './PersonalInfo'
import { getHealthieRequest, useApplyTags } from 'hooks/careHooks'
import { setBrazeProfile } from 'utils/brazeInit'
import { useSkuCondition } from 'hooks/useSkuCondiction'
import { useLoginSuccess } from 'hooks/useAutoLogin'
import { HEALTHIE_GET_USER_STRING } from 'apis/HealthieApiClient'
import { AuthContext } from 'contexts/AuthContext'
const VerifyEmail = (props) => {
  const { sku, supportForNewWelcome, email: fitonEmail } = props
  const { rehabSelectedThemes } = useRehabSelectedThemes()
  const { i18n } = useI18n()
  const { personalInfo } = usePersonalInfo()
  const { applyHealthieTags } = useApplyTags()
  const [code, setCode] = useState([])
  const [isSubmit, setIsSubmit] = useState(false)
  const [healthieUser, setHealthieUser] = useState({})
  const authContext = useContext(AuthContext)
  const [placeholder0, setPlaceholder0] = useState('-')
  const [placeholder1, setPlaceholder1] = useState('-')
  const [placeholder2, setPlaceholder2] = useState('-')
  const [placeholder3, setPlaceholder3] = useState('-')
  const placeholders = [placeholder0, placeholder1, placeholder2, placeholder3]
  const setPlaceholders = [
    setPlaceholder0,
    setPlaceholder1,
    setPlaceholder2,
    setPlaceholder3,
  ]
  const codeRef0 = useRef()
  const codeRef1 = useRef()
  const codeRef2 = useRef()
  const codeRef3 = useRef()
  const refs = [codeRef0, codeRef1, codeRef2, codeRef3]
  const { getNewSkuCondition } = useSkuCondition()
  const { openSkipScreen } = useLoginSuccess({
    sku,
    supportForNewWelcome,
    setCurrentKey: props.setCurrentKey,
    fitonEmail,
    email: personalInfo.email || getUserEmail(),
    ...props,
  })

  useHealthieInfo({ user: healthieUser, supportForNewWelcome })

  useEffect(() => {
    logAmplitudeEvent('Screen View: Magic Link Email Sent', {
      Source: 'Onboarding',
      sku: props.sku,
      theme: rehabSelectedThemes?.map((theme) => theme.name).join(','),
      language: getLocalLanguage(),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getCode = useMemo(() => {
    let codeString = ''
    code.forEach((item) => {
      codeString += item
    })
    return codeString
  }, [code])

  const onCodeChange = (index, value) => {
    const _code = [...code]
    _code[index] = value
    setCode(_code)
    if (value) {
      switch (index) {
        case 0:
          codeRef1.current.focus()
          break
        case 1:
          codeRef2.current.focus()
          break
        case 2:
          codeRef3.current.focus()
          break
        default:
          break
      }
    }
  }

  const updateUserHealthieInfo = useCallback(async () => {
    const { data } = await getHealthieRequest({
      queryString: HEALTHIE_GET_USER_STRING,
      variables: { id: getHealthieUserId(), include_tags: true },
      operationName: 'getUser',
    })
    setHealthieUser(data?.user)
  }, [])

  const submitCode = useCallback(
    async (e) => {
      e && e.preventDefault()
      const codeStr = getCode
      if (codeStr.length < 4) {
        return
      }
      setIsSubmit(true)
      setTimeout(() => {
        window.scrollTo(0, 0)
      }, 30)
      const response = await verifyMagicLinkByDigitCodePromise({
        digitCode: codeStr,
        email: personalInfo.email || getUserEmail(),
        stateId: personalInfo.state.id,
      })
      if (response && response.code === 200) {
        const user = response.data
        setCode([])
        setIsSubmit(false)
        props.onLoginSuccess({ ...user, stateType: personalInfo.state.type })
        applySkuTag(sku, personalInfo.state, applyHealthieTags)
        setBrazeProfile({ sku })
        const themeIds = rehabSelectedThemes?.map((theme) => theme.id)
        const skuCondition = getNewSkuCondition({
          skuCondition: user?.skuCondition,
          themeIds,
          sku,
        })
        const userStatus = user?.skuStatus?.[sku]
        updateUserPromise(
          {
            ...(userStatus !== 2 && {
              skuStatus: {
                sku,
                status: 1,
              },
            }),
            ...(!_.isEmpty(skuCondition) && { skuCondition }),
          },
          authContext
        )

        await updateUserHealthieInfo()
        setStateId(personalInfo.state.id)
        openSkipScreen(personalInfo.state, sku)
      } else {
        setIsSubmit(false)
        showNotification({
          type: 'error',
          title: i18n(response?.msg || 'Verify failed'),
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getCode, personalInfo.email]
  )

  const sendEmail = async (resend) => {
    const resp = await emailMagicLinkPromise({
      email: personalInfo.email || getUserEmail(),
      stateId: personalInfo.state.id,
    })
    if (resp.code !== 200)
      return showNotification({
        type: 'error',
        title: 'Error',
        message: i18n(resp.msg),
      })
    if (resend) {
      showNotification({
        type: 'success',
        title: i18n('Resend success'),
      })
    }
  }

  useEffect(() => {
    submitCode()
  }, [submitCode])

  return (
    <div className='info-sec'>
      <ContentHeader {...props} />
      <div className='info-content'>
        <div className='title'>{i18n('Check your email')}</div>
        <p
          className='subtitle-1'
          style={isMobile() ? { marginBottom: '32px' } : {}}
        >
          {i18n(
            'We sent an email to {0}. Please check your inbox and click on the link to validate',
            <span className='gradient-font-1'>
              {personalInfo.email || getUserEmail()}
            </span>
          )}
        </p>
        <p className='subtitle-1'>
          {i18n(
            `You can also enter the 4 digit code you've received in your email to validate your email.`
          )}
        </p>
        <form onSubmit={submitCode}>
          <div className='code-sec'>
            {new Array(4).fill(0).map((n, index) => (
              <Input
                key={index}
                className={classnames('code-value', { hasValue: code[index] })}
                maxLength={1}
                value={code[index]}
                ref={(input) => {
                  refs[index].current = input
                }}
                onChange={(e) => {
                  let value = e.target.value ? e.target.value.toString() : ''
                  if (value.length > 1) {
                    setCode(value?.slice(0, 4).split(''))
                  } else {
                    onCodeChange(index, value)
                  }
                }}
                placeholder={placeholders[index]}
                onFocus={() => {
                  setPlaceholders[index]('')
                }}
                onBlur={() => {
                  setPlaceholders[index]('-')
                }}
                type='number'
              />
            ))}
          </div>
          <Button
            className='btn-primary full-width-btn'
            style={isMobile() ? { width: '100%' } : {}}
            onClick={submitCode}
            disabled={getCode.length < 4 || isSubmit}
          >
            {i18n('Verify')}
          </Button>
        </form>
        <p className='resend-sec'>
          {i18n(`Didn't get your email?`)}{' '}
          <span onClick={() => sendEmail(true)}>{i18n(`Resend email`)}</span>
        </p>
      </div>
    </div>
  )
}

export default VerifyEmail
